import React, { useState, useCallback } from 'react'
import {
  Eye,
  EyeOff,
  Key,
  Building2,
  Layers,
  CheckSquare,
  Loader2,
  ExternalLink,
  ChevronRight,
  Zap,
  Clock,
  CalendarClock,
  RefreshCw,
} from 'lucide-react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { Skeleton } from '../ui/skeleton'
import {
  useGetClickupConfigQuery,
  useLazyGetClickupTokenQuery,
  useGetScheduleQuery,
  useLazyGetOpenaiConfigQuery,
} from '@/redux/service/configuration'
import {
  ClickupConfigResponse,
  ClickupWorkspace,
} from '@/redux/service/datascope'
import { Link } from 'react-router-dom'
import { Button } from '../ui/button'
import ConfigurationMode from '../ConfigurationMode'
import ScheduleUpdateModal from './ScheduleUpdateModal'
import OpenaiUpdateModal from './OpenaiUpdateModal'
const SectionHeader = ({
  icon: Icon,
  label,
  count,
}: {
  icon: React.ElementType
  label: string
  count?: number
}) => (
  <div className='flex items-center gap-2 mb-4'>
    <div className='w-8 h-8 rounded-lg bg-muted flex items-center justify-center text-muted-foreground'>
      <Icon className='w-4 h-4' />
    </div>
    <span className='text-sm font-semibold tracking-tight text-foreground'>
      {label}
    </span>
    {count !== undefined && (
      <span className='text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground uppercase'>
        {count}
      </span>
    )}
  </div>
)

const PillBadge = ({
  children,
  archived,
}: {
  children: React.ReactNode
  archived?: boolean
}) => (
  <span
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
      archived
        ? 'bg-muted/50 text-muted-foreground border-border/50'
        : 'bg-background text-foreground border-border hover:border-primary/50'
    }`}
  >
    <span
      className={`w-1.5 h-1.5 rounded-full ${archived ? 'bg-muted-foreground/30' : 'bg-primary'}`}
    />
    {children}
  </span>
)

dayjs.extend(utc)
dayjs.extend(timezone)

const ConfigSkeleton = () => (
  <div className='space-y-3 animate-pulse'>
    <div className='flex flex-wrap gap-2'>
      {[1, 2, 3].map(i => (
        <Skeleton key={i} className='h-8 w-28 rounded-xl' />
      ))}
    </div>
  </div>
)

const Configuration = () => {
  const [tokenVisible, setTokenVisible] = useState(false)
  const [revealedToken, setRevealedToken] = useState<string | null>(null)
  const [tokenError, setTokenError] = useState<string | null>(null)
  const [isConfigModeOpen, setIsConfigModeOpen] = useState<boolean>(false)
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState<boolean>(false)
  const [isOpenaiModalOpen, setIsOpenaiModalOpen] = useState<boolean>(false)
  const [fetchToken, { isFetching: isTokenFetching }] =
    useLazyGetClickupTokenQuery()
  const [fetchOpenaiToken, { isFetching: isOpenaiTokenFetching }] =
    useLazyGetOpenaiConfigQuery()

  const [openaiVisible, setOpenaiVisible] = useState(false)
  const [revealedOpenaiToken, setRevealedOpenaiToken] = useState<string | null>(
    null
  )
  const [openaiTokenError, setOpenaiTokenError] = useState<string | null>(null)

  const { data: configData, isLoading: isConfigLoading } =
    useGetClickupConfigQuery()

  const { data: scheduleResponse, isLoading: isScheduleLoading } =
    useGetScheduleQuery()

  const config = (configData as ClickupConfigResponse | undefined)?.data
  const scheduleData = scheduleResponse?.data

  const handleToggleToken = useCallback(async () => {
    if (tokenVisible) {
      setTokenVisible(false)
      return
    }

    if (revealedToken) {
      setTokenVisible(true)
      return
    }

    try {
      setTokenError(null)
      const result = await fetchToken().unwrap()
      const token = (result as { message: string; data: string })?.data ?? null
      setRevealedToken(token)
      setTokenVisible(true)
    } catch {
      setTokenError('Failed to fetch token. Please try again.')
    }
  }, [tokenVisible, revealedToken, fetchToken])

  const handleToggleOpenaiToken = useCallback(async () => {
    if (openaiVisible) {
      setOpenaiVisible(false)
      return
    }

    if (revealedOpenaiToken) {
      setOpenaiVisible(true)
      return
    }

    try {
      setOpenaiTokenError(null)
      const result = await fetchOpenaiToken().unwrap()
      const token = (result as { message: string; data: string })?.data ?? null
      setRevealedOpenaiToken(token)
      setOpenaiVisible(true)
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ||
        'Failed to fetch OpenAI token. Please try again.'
      setOpenaiTokenError(message)
    }
  }, [openaiVisible, revealedOpenaiToken, fetchOpenaiToken])

  const maskedToken = 'pk_●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●'
  const displayToken =
    tokenVisible && revealedToken ? revealedToken : maskedToken

  return (
    <div className='h-full flex flex-col animate-in fade-in slide-in-from-bottom-2 duration-500 space-y-10 overflow-hidden'>
      <div className='flex justify-end shrink-0'>
        <Button
          variant='default'
          className='h-9 px-6 rounded-lg  shadow-lg shadow-primary/20 active:scale-95 transition-all'
          onClick={() => setIsConfigModeOpen(true)}
        >
          Update Configuration
        </Button>
      </div>

      <div className='flex-1 overflow-auto space-y-10 pr-2 custom-scrollbar'>
        {/* ClickUp API Token Section */}
        <div className='rounded-2xl border border-border  overflow-hidden'>
          <div className='flex items-center justify-between px-6 py-5 border-b border-border bg-muted/80'>
            <div className='flex items-center gap-4'>
              <div className='w-10 h-10 rounded-xl bg-muted flex items-center justify-center text-muted-foreground'>
                <Key className='w-5 h-5' />
              </div>
              <div>
                <h3 className='text-sm font-bold text-foreground'>
                  ClickUp API Token
                </h3>
                <p className='text-xs text-muted-foreground mt-0.5'>
                  Personal access token for ClickUp integration
                </p>
              </div>
            </div>
            <Link
              to='https://app.clickup.com/settings/apps'
              target='_blank'
              rel='noopener noreferrer'
              className='flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition-colors'
            >
              ClickUp Settings
              <ExternalLink className='w-3.5 h-3.5' />
            </Link>
          </div>

          <div className='px-6 py-6'>
            <div
              className={`group relative flex items-center min-h-[3.5rem] rounded-xl border bg-muted/80 px-5 font-mono text-sm transition-all duration-300 ${
                tokenVisible
                  ? 'border-primary/50 bg-background'
                  : 'border-border'
              }`}
            >
              <span
                className={`flex-1 select-none transition-all duration-500 break-all ${
                  tokenVisible
                    ? 'text-foreground tracking-normal'
                    : 'text-muted-foreground tracking-[0.2em]'
                }`}
              >
                {isTokenFetching ? (
                  <span className='flex items-center gap-2 text-muted-foreground animate-pulse'>
                    <Loader2 className='w-4 h-4 animate-spin' />
                    Fetching...
                  </span>
                ) : (
                  displayToken
                )}
              </span>

              <button
                type='button'
                onClick={handleToggleToken}
                disabled={isTokenFetching}
                className='ml-4 p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all active:scale-90'
              >
                {tokenVisible ? (
                  <Eye className='w-4 h-4' />
                ) : (
                  <EyeOff className='w-4 h-4' />
                )}
              </button>
            </div>

            {tokenError ? (
              <p className='mt-3 text-[11px] text-destructive flex items-center gap-2'>
                <span className='w-1 h-1 rounded-full bg-destructive' />
                {tokenError}
              </p>
            ) : (
              <p className='mt-3 text-[11px] text-muted-foreground/70'>
                Click the eye icon to reveal your ClickUp personal API token.
                Keep this token private.
              </p>
            )}
          </div>
        </div>

        {/* OpenAI API Token Section */}
        <div className='rounded-2xl border border-border  overflow-hidden'>
          <div className='flex items-center justify-between px-6 py-5 border-b border-border bg-muted/80'>
            <div className='flex items-center gap-4'>
              <div className='w-10 h-10 rounded-xl bg-muted flex items-center justify-center text-muted-foreground'>
                <Zap className='w-5 h-5' />
              </div>
              <div>
                <h3 className='text-sm font-bold text-foreground'>
                  OpenAI API Token
                </h3>
                <p className='text-xs text-muted-foreground mt-0.5'>
                  API key for OpenAI insights and analysis
                </p>
              </div>
            </div>
            <div className='flex items-center gap-3'>
              <Button
                variant='outline'
                size='sm'
                className='h-7 rounded-full border-border hover:bg-muted text-[11px] font-bold px-4'
                onClick={() => setIsOpenaiModalOpen(true)}
              >
                Update
              </Button>
              <Link
                to='https://platform.openai.com/api-keys'
                target='_blank'
                rel='noopener noreferrer'
                className='flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition-colors'
              >
                OpenAI Settings
                <ExternalLink className='w-3.5 h-3.5' />
              </Link>
            </div>
          </div>

          <div className='px-6 py-6'>
            <div
              className={`group relative flex items-center min-h-[3.5rem] rounded-xl border bg-muted/80 px-5 font-mono text-sm transition-all duration-300 ${
                openaiVisible
                  ? 'border-primary/50 bg-background'
                  : 'border-border'
              }`}
            >
              <span
                className={`flex-1 select-none transition-all duration-500 break-all ${
                  openaiVisible
                    ? 'text-foreground tracking-normal'
                    : 'text-muted-foreground tracking-[0.2em]'
                }`}
              >
                {isOpenaiTokenFetching ? (
                  <span className='flex items-center gap-2 text-muted-foreground animate-pulse'>
                    <Loader2 className='w-4 h-4 animate-spin' />
                    Fetching...
                  </span>
                ) : openaiVisible && revealedOpenaiToken ? (
                  revealedOpenaiToken
                ) : (
                  'sk-●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●'
                )}
              </span>

              <button
                type='button'
                onClick={handleToggleOpenaiToken}
                disabled={isOpenaiTokenFetching}
                className='ml-4 p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all active:scale-90'
              >
                {openaiVisible ? (
                  <Eye className='w-4 h-4' />
                ) : (
                  <EyeOff className='w-4 h-4' />
                )}
              </button>
            </div>

            {openaiTokenError ? (
              <p className='mt-3 text-[11px] text-destructive flex items-center gap-2'>
                <span className='w-1 h-1 rounded-full bg-destructive' />
                {openaiTokenError}
              </p>
            ) : (
              <p className='mt-3 text-[11px] text-muted-foreground/70'>
                Click the eye icon to reveal your OpenAI API key. This key is
                used for AI-powered description analysis.
              </p>
            )}
          </div>
        </div>

        {/* Automation Schedule Section */}
        <div className='rounded-2xl border border-border  overflow-hidden'>
          <div className='flex items-center justify-between px-6 py-5 border-b border-border bg-muted/80'>
            <div className='flex items-center gap-4'>
              <div className='w-10 h-10 rounded-xl bg-muted flex items-center justify-center text-muted-foreground'>
                <CalendarClock className='w-5 h-5' />
              </div>
              <div>
                <h3 className='text-sm font-bold text-foreground'>
                  Automation Schedule
                </h3>
                <p className='text-xs text-muted-foreground mt-0.5'>
                  Auto-sync frequency and next scheduled run
                </p>
              </div>
            </div>
            <Button
              variant='outline'
              size='sm'
              className='h-7 rounded-full border-border hover:bg-muted text-[11px] font-bold px-4'
              onClick={() => setIsScheduleModalOpen(true)}
            >
              Update
            </Button>
          </div>

          <div className='px-6 py-8'>
            {isScheduleLoading ? (
              <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className='h-24 rounded-2xl' />
                ))}
              </div>
            ) : (
              <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                <div className='p-6 rounded-2xl border border-border bg-muted/20 flex flex-col gap-4 group transition-colors hover:bg-muted/40'>
                  <div className='flex items-center gap-2 text-muted-foreground'>
                    <RefreshCw className='w-3.5 h-3.5 group-hover:rotate-180 transition-transform duration-1000' />
                    <span className='text-[10px] font-bold uppercase tracking-[0.1em]'>
                      Sync Frequency
                    </span>
                  </div>
                  <div className='flex items-baseline gap-1.5'>
                    <span className='text-3xl font-bold tracking-tight text-foreground leading-none'>
                      {scheduleData?.sync_frequency || '-'}
                    </span>
                    <span className='text-sm font-medium text-muted-foreground'>
                      hours
                    </span>
                  </div>
                </div>

                <div className='p-6 rounded-2xl border border-border bg-muted/20 flex flex-col gap-4 group transition-colors hover:bg-muted/40'>
                  <div className='flex items-center gap-2 text-muted-foreground'>
                    <Clock className='w-3.5 h-3.5' />
                    <span className='text-[10px] font-bold uppercase tracking-[0.1em]'>
                      Scheduled Hour
                    </span>
                  </div>
                  <div className='text-3xl font-bold tracking-tight text-foreground leading-none'>
                    {scheduleData?.scheduled_hour !== undefined
                      ? (() => {
                          const h = scheduleData.scheduled_hour % 24
                          const ampm = h >= 12 ? 'PM' : 'AM'
                          const displayHour = h % 12 || 12
                          return `${displayHour} ${ampm}`
                        })()
                      : '-'}
                  </div>
                </div>

                <div className='p-6 rounded-2xl border border-primary/10 bg-primary/[0.03] flex flex-col gap-4 transition-colors hover:bg-primary/[0.05]'>
                  <div className='flex items-center gap-2 text-primary'>
                    <Zap className='w-3.5 h-3.5' />
                    <span className='text-[10px] font-bold uppercase tracking-[0.1em]'>
                      Next Run
                    </span>
                  </div>
                  <div className='flex flex-col gap-1'>
                    <span className='text-lg font-bold text-foreground leading-none'>
                      {scheduleData?.next_run_at
                        ? dayjs
                            .utc(scheduleData.next_run_at)
                            .local()
                            .format('MMM D, YYYY')
                        : '-'}
                    </span>
                    <span className='text-sm font-medium text-primary'>
                      {scheduleData?.next_run_at
                        ? dayjs
                            .utc(scheduleData.next_run_at)
                            .local()
                            .format('h:mm A')
                        : 'Not scheduled'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ClickUp Configuration Details Section */}
        <div className='rounded-2xl border border-border  overflow-hidden'>
          <div className='flex items-center justify-between px-6 py-5 border-b border-border bg-muted/80'>
            <div className='flex items-center gap-4'>
              <div className='w-10 h-10 rounded-xl bg-muted flex items-center justify-center text-muted-foreground'>
                <Layers className='w-5 h-5' />
              </div>
              <div>
                <h3 className='text-sm font-bold text-foreground'>
                  ClickUp Configuration
                </h3>
                <p className='text-xs text-muted-foreground mt-0.5'>
                  Synced Workspaces and Spaces from ClickUp
                </p>
              </div>
            </div>
          </div>

          <div className='p-6 space-y-10'>
            {isConfigLoading ? (
              <div className='space-y-8'>
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className='space-y-3'>
                    <Skeleton className='h-6 w-36 rounded-lg' />
                    <ConfigSkeleton />
                  </div>
                ))}
              </div>
            ) : config ? (
              <div className='space-y-12'>
                {config.workspaces && config.workspaces.length > 0 && (
                  <div>
                    <SectionHeader
                      icon={Building2}
                      label='Workspaces'
                      count={config.workspaces.length}
                    />
                    <div className='flex flex-wrap gap-3'>
                      {config.workspaces.map((ws: ClickupWorkspace) => (
                        <div
                          key={ws.id}
                          className='group flex items-center gap-3 px-4 py-2.5 rounded-xl border border-border bg-background hover:border-primary/50 transition-all cursor-default'
                        >
                          <div className='w-6 h-6 rounded bg-muted flex items-center justify-center text-[10px] font-bold text-muted-foreground'>
                            {ws.name.charAt(0).toUpperCase()}
                          </div>
                          <span className='text-sm font-medium text-foreground capitalize'>
                            {ws.name}
                          </span>
                          <ChevronRight className='w-3.5 h-3.5 text-muted-foreground/30 group-hover:text-primary transition-colors' />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {config.spaces && config.spaces.length > 0 && (
                  <div className='space-y-8'>
                    <div className='h-px bg-border/50' />
                    <SectionHeader
                      icon={Layers}
                      label='Spaces'
                      count={config.spaces.length}
                    />

                    {(() => {
                      const activeSpaces = config.spaces!.filter(
                        sp => !sp.archived
                      )
                      const archivedSpaces = config.spaces!.filter(
                        sp => sp.archived
                      )
                      return (
                        <div className='space-y-8'>
                          {activeSpaces.length > 0 && (
                            <div className='pl-10'>
                              <div className='flex items-center gap-2 mb-4'>
                                <span className='text-[10px] font-bold text-primary uppercase tracking-widest'>
                                  Active
                                </span>
                                <div className='h-px flex-1 bg-border/50' />
                              </div>
                              <div className='flex flex-wrap gap-2 capitalize'>
                                {activeSpaces.map(sp => (
                                  <PillBadge key={sp.id} archived={false}>
                                    {sp.name}
                                  </PillBadge>
                                ))}
                              </div>
                            </div>
                          )}

                          {archivedSpaces.length > 0 && (
                            <div className='pl-10'>
                              <div className='flex items-center gap-2 mb-4'>
                                <span className='text-[10px] font-bold text-muted-foreground/60 uppercase tracking-widest'>
                                  Archived
                                </span>
                                <div className='h-px flex-1 bg-border/50' />
                              </div>
                              <div className='flex flex-wrap gap-2 capitalize'>
                                {archivedSpaces.map(sp => (
                                  <PillBadge key={sp.id} archived={true}>
                                    {sp.name}
                                  </PillBadge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })()}
                  </div>
                )}

                {!config.workspaces?.length && !config.spaces?.length && (
                  <div className='py-12 flex flex-col items-center gap-4 text-center'>
                    <div className='w-12 h-12 rounded-2xl bg-muted flex items-center justify-center'>
                      <CheckSquare className='w-6 h-6 text-muted-foreground/30' />
                    </div>
                    <div className='space-y-1'>
                      <p className='text-sm font-semibold text-foreground'>
                        No configuration data
                      </p>
                      <p className='text-xs text-muted-foreground'>
                        Sync your ClickUp account to see workspaces & spaces.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className='py-12 flex flex-col items-center gap-4 text-center'>
                <div className='w-12 h-12 rounded-2xl bg-primary/5 flex items-center justify-center text-primary'>
                  <Zap className='w-6 h-6' />
                </div>
                <div className='space-y-1'>
                  <p className='text-sm font-semibold text-foreground'>
                    ClickUp integration pending
                  </p>
                  <p className='text-xs text-muted-foreground'>
                    Configure your API token to begin syncing workspaces.
                  </p>
                </div>
              </div>
            )}
          </div>
          <ConfigurationMode
            isOpen={isConfigModeOpen}
            isClose={() => setIsConfigModeOpen(false)}
          />
          <ScheduleUpdateModal
            isOpen={isScheduleModalOpen}
            onClose={() => setIsScheduleModalOpen(false)}
            initialData={{
              sync_frequency: scheduleData?.sync_frequency,
              scheduled_hour: scheduleData?.scheduled_hour,
            }}
          />
          <OpenaiUpdateModal
            isOpen={isOpenaiModalOpen}
            onClose={() => setIsOpenaiModalOpen(false)}
            initialToken={revealedOpenaiToken || ''}
            onSuccess={() => {
              setRevealedOpenaiToken(null)
              setOpenaiVisible(false)
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default Configuration
